import { useState } from "react";
import { Loader2, KeyRound, Check, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AccountSettings({ user }) {
  return (
    <div className="max-w-2xl" data-testid="account-settings">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-ink-soft mb-1">
          Account
        </div>
        <h2 className="font-serif text-3xl text-ink">Your sign-in details.</h2>
        <p className="text-sm text-ink-soft mt-2">
          Signed in as{" "}
          <span className="text-ink font-medium">{user?.email}</span>
        </p>
      </div>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmNext, setConfirmNext] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const minLen = next.length >= 8;
  const hasUpper = /[A-Z]/.test(next);
  const hasNumber = /\d/.test(next);
  const matches = next && next === confirmNext;
  const canSubmit =
    current && minLen && matches && !loading && current !== next;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/admin/change-password", {
        current_password: current,
        new_password: next,
      });
      setSuccess(true);
      setCurrent("");
      setNext("");
      setConfirmNext("");
      toast.success("Password updated. Next time you sign in, use your new password.");
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Couldn't update password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="change-password-form"
      className="bg-cream-soft border border-sand/70 rounded-[2rem] p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-full bg-navy/10 text-navy flex items-center justify-center">
          <KeyRound size={16} />
        </div>
        <div>
          <div className="font-serif text-xl text-ink">Change password</div>
          <div className="text-xs text-ink-soft">
            Pick something only you'd know — at least 8 characters.
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Field
          label="Current password"
          type={showPw ? "text" : "password"}
          value={current}
          onChange={setCurrent}
          testid="change-pw-current"
          autoComplete="current-password"
        />
        <Field
          label="New password"
          type={showPw ? "text" : "password"}
          value={next}
          onChange={setNext}
          testid="change-pw-new"
          autoComplete="new-password"
        />
        <Field
          label="Confirm new password"
          type={showPw ? "text" : "password"}
          value={confirmNext}
          onChange={setConfirmNext}
          testid="change-pw-confirm"
          autoComplete="new-password"
        />

        <button
          type="button"
          onClick={() => setShowPw((s) => !s)}
          className="inline-flex items-center gap-2 text-xs text-ink-soft hover:text-navy transition-colors"
          data-testid="change-pw-toggle-visibility"
        >
          {showPw ? <EyeOff size={12} /> : <Eye size={12} />}
          {showPw ? "Hide passwords" : "Show passwords"}
        </button>

        {next && (
          <ul className="text-xs space-y-1 mt-2 pl-1">
            <Req ok={minLen} text="At least 8 characters" />
            <Req ok={hasUpper} text="One uppercase letter (recommended)" soft />
            <Req ok={hasNumber} text="One number (recommended)" soft />
            <Req
              ok={!!matches}
              text={
                confirmNext
                  ? matches
                    ? "Passwords match"
                    : "Passwords don't match"
                  : "Retype your new password to confirm"
              }
            />
          </ul>
        )}

        {error && (
          <div
            data-testid="change-pw-error"
            className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded-xl px-4 py-3"
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div
            data-testid="change-pw-success"
            className="flex items-start gap-2 text-sm text-navy bg-navy/5 border border-navy/20 rounded-xl px-4 py-3"
          >
            <Check size={16} className="mt-0.5 flex-shrink-0" />
            <span>Password updated. Your next sign-in will use the new one.</span>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="change-pw-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-navy text-cream px-7 py-3.5 text-sm font-medium hover:bg-navy-deep transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : null}
          Update password
        </button>
      </div>
    </form>
  );
}

function Field({ label, type, value, onChange, testid, autoComplete }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-2 block">
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className="w-full bg-cream border border-sand/80 rounded-2xl px-5 py-3.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy"
      />
    </div>
  );
}

function Req({ ok, text, soft }) {
  return (
    <li
      className={`flex items-center gap-2 ${
        ok ? "text-navy" : soft ? "text-ink-soft/60" : "text-ink-soft"
      }`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          ok ? "bg-navy" : "bg-ink-soft/40"
        }`}
      />
      {text}
    </li>
  );
}
